import { FC, useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import Icon from "../../../../components/Icon/Icon";
import {
  IconColorE,
  IconSizeE,
  StatusE,
  UserRoleE,
  VideoContentStatusE,
} from "../../../../models/enums";
import { clss } from "../../../../utils/helpers/styleHelpers";
import ReprocessIcon from "../../../../components/ReprocessIcon/ReprocessIcon";
import AccessRights from "../../../../components/AccessRights/AccessRights";
import { useAppSelector } from "../../../../store/hooks";
import { selectCurrentUser } from "../../../../store/selectors/authSelectors";

import styles from "./videoStatusIcon.module.scss";

type VideoStatusIconPropTypes = {
  status: VideoContentStatusE;
  projectStatus: StatusE;
  Id: number;
  UploadDateTime: string | Date;
  EncodingErrorDescription: string;
  onPlay: () => void;
  isFetchingData: boolean;
};

const VideoStatusIcon: FC<VideoStatusIconPropTypes> = ({
  status,
  onPlay,
  UploadDateTime,
  projectStatus,
  EncodingErrorDescription,
  Id,
  isFetchingData,
}) => {
  const [isReprocessShown, setIsReprocessShown] = useState(false);
  const user = useAppSelector(selectCurrentUser);

  useEffect(() => {
    if (isReprocessShown ||
      status === VideoContentStatusE.UPLOADING ||
      status === VideoContentStatusE.ENCODING ||
      status === VideoContentStatusE.WAITING) {
      return;
    }

    const uploadTime = dayjs(UploadDateTime);
    const currentTime = dayjs();
    const timeElapsedMs = currentTime.diff(uploadTime, 'millisecond');
    const remainingMs = 60000 - timeElapsedMs;

    if (remainingMs <= 0) {
      setIsReprocessShown(true);
      return;
    }

    const timerId = setTimeout(() => {
      setIsReprocessShown(true);
    }, remainingMs);

    return () => {
      clearTimeout(timerId);
    };
  }, [status, UploadDateTime, isReprocessShown, isFetchingData]);

  const UploadingIcon = useCallback(
    () => (
      <div className={styles.iconWrapper}>
        <Icon
          name="loading"
          color={IconColorE.GRAY}
          size={IconSizeE.S}
          title={status}
        />
      </div>
    ),
    [status]
  );

  const EncodingIcon = useCallback(
    () => (
      <div className={styles.iconWrapper}>
        <Icon
          name="encoding"
          color={IconColorE.GRAY}
          size={IconSizeE.S}
          title={status}
        />
      </div>
    ),
    [status]
  );

  const Reprocess = useCallback(
    () => (
      <AccessRights
        accessFor={[UserRoleE.PostProduction, UserRoleE.DeliveryService]}
      >
        <ReprocessIcon projectFileId={Id} projectStatus={projectStatus} />
      </AccessRights>
    ),
    [projectStatus, Id]
  );

  const PlayIcon = useCallback(
    () => (
      <div className={styles.iconPlayWrapper}>
        <Icon
          name="play"
          color={IconColorE.WHITE}
          title="Uploaded and encoded"
          onClick={onPlay}
          className={styles.iconPlay}
        />
      </div>
    ),
    [status]
  );

  const LowResOnlyIcon = useCallback(
    () => (
      <div className={styles.iconPlayWrapper} onClick={onPlay}>
        <Icon name="versionLowResOnly" title="Low resolution available only" />
      </div>
    ),
    [status]
  );

  const WaitingIcon = useCallback(
    () => (
      <div className={styles.iconWrapper}>
        <Icon name="clockFilled" color={IconColorE.GRAY} title={status} />
      </div>
    ),
    [status]
  );

  if (projectStatus === StatusE.InnerCampaign) {
    return null;
  }

  switch (status) {
    case VideoContentStatusE.WAITING:
      return <WaitingIcon />;
    case VideoContentStatusE.UPLOADING:
      return isReprocessShown &&
        (user?.role === UserRoleE.PostProduction ||
          user?.role === UserRoleE.DeliveryService) ? (
        <Reprocess />
      ) : (
        <UploadingIcon />
      );
    case VideoContentStatusE.UPLOADED:
      return isReprocessShown &&
        (user?.role === UserRoleE.PostProduction ||
          user?.role === UserRoleE.DeliveryService) ? (
        <Reprocess />
      ) : (
        <UploadingIcon />
      );
    case VideoContentStatusE.ENCODING:
      return isReprocessShown &&
        (user?.role === UserRoleE.PostProduction ||
          user?.role === UserRoleE.DeliveryService) ? (
        <Reprocess />
      ) : (
        <EncodingIcon />
      );
    case VideoContentStatusE.ENCODED:
      return <PlayIcon />;
    case VideoContentStatusE.LOW_RES_ONLY:
      return <LowResOnlyIcon />;
    case VideoContentStatusE.ENCODING_ERROR:
      return (
        <div className={clss([styles.errorIconWrapper, styles.iconWrapper])}>
          <Icon
            name="versionError"
            title={EncodingErrorDescription}
            withoutTranslation
          />
        </div>
      );
    case VideoContentStatusE.UPLOADED_ERROR:
      return (
        <div className={clss([styles.errorIconWrapper, styles.iconWrapper])}>
          <Icon name="versionError" title="Uploading error" />
        </div>
      );
    case VideoContentStatusE.DELETED:
      return (
        <div className={clss([styles.errorIconWrapper, styles.iconWrapper])}>
          <Icon name="versionDeleted" title="File deleted" />
        </div>
      );
    default:
      return <WaitingIcon />;
  }
};

export default VideoStatusIcon;
